import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { ref, onValue, set } from "firebase/database";
import { db } from "../firebase";

import EventModal from "./EventModal";
import "./calendar.css";

const localizer = momentLocalizer(moment);

export default function CalendarPage({ isAdmin }) {
  const [events, setEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date()); // ✅ only once

  // ============================
  // LOAD EVENTS FROM FIREBASE
  // ============================
  useEffect(() => {
    const eventsRef = ref(db, "calendarEvents");

    const unsubscribe = onValue(eventsRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setEvents([]);
        return;
      }

      const loadedEvents = Object.keys(data).map((dateKey) => ({
        id: dateKey,
        title: data[dateKey].title,
        description: data[dateKey].description || "",
        start: new Date(dateKey),
        end: new Date(dateKey),
        color: "#1976d2",
      }));

      setEvents(loadedEvents);
    });

    return () => unsubscribe();
  }, []);

  // ============================
  // ADMIN: CLICK DATE
  // ============================
  const handleSelectSlot = ({ start }) => {
    if (!isAdmin) return;
    setSelectedDate(start);
    setModalOpen(true);
  };

  // ============================
  // ADMIN: SAVE EVENT
  // ============================
  const handleSaveEvent = (event) => {
    const dateKey = event.start.toISOString().split("T")[0];
    const eventRef = ref(db, "calendarEvents/" + dateKey);

    set(eventRef, {
      title: event.title,
      description: event.notes || "",
    });

    setModalOpen(false);
  };

  // ============================
  // CLICK EVENT
  // ============================
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  // ============================
  // MAKE LINK CLICKABLE
  // ============================
  const renderDescription = (text) => {
    if (!text) return null;

    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/;

    return text.split(" ").map((word, index) => {
      if (urlRegex.test(word)) {
        const href = word.startsWith("http")
          ? word
          : "https://" + word;

        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="event-link"
          >
            {word}{" "}
          </a>
        );
      }
      return word + " ";
    });
  };

  return (
    <div className="calendar-wrapper">
      <h2 className="calendar-title">📅 Scheduled Events</h2>

      {/* ================= CALENDAR ================= */}
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        selectable={isAdmin}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        onNavigate={(date) => setCurrentDate(date)}  // ✅ clean
        className="calendar-view"
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.color,
            color: "#fff",
            borderRadius: "6px",
            padding: "4px",
            cursor: "pointer",
          },
        })}
      />

      {/* ================= EVENT LIST ================= */}
      <div className="event-list-section">
        <h3>Dates with Scheduled Events</h3>

        {events.filter(event =>
          moment(event.start).isSame(moment(currentDate), "month")
        ).length === 0 && (
          <p>No events scheduled for this month.</p>
        )}

        {events
          .filter(event =>
            moment(event.start).isSame(moment(currentDate), "month")
          )
          .map((event) => (
            <div
              key={event.id}
              className="event-list-item"
              onClick={() => setSelectedEvent(event)}
            >
              • {moment(event.start).format("YYYY-MM-DD")} — {event.title}
            </div>
          ))}
      </div>

      {/* ================= POPUP ================= */}
      {selectedEvent && (
        <div
          className="event-popup-backdrop"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="event-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="event-title">Event Details</div>

            <div className="event-field">
              <div className="field-label">Title</div>
              <div className="field-value">{selectedEvent.title}</div>
            </div>

            <div className="event-field">
              <div className="field-label">Description</div>
              <div className="field-value">
                {renderDescription(selectedEvent.description)}
              </div>
            </div>

            <div className="event-popup-footer">
              <button onClick={() => setSelectedEvent(null)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ================= ADMIN MODAL ================= */}
      {isAdmin && (
        <EventModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveEvent}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
}
